import React, { useState, useEffect } from "react";
import { Spinner, Text, Box } from "@chakra-ui/react";
import MetricCard from "./MetricCard";
import { metricsService, DealSentimentSummary } from "../../services/metricsService";

interface DealSentimentCardProps {
  onMetricSelect?: (metricName: string) => void;
  businessIdentifier?: string;
  timeRange?: string;
  value?: number;
  trendValue?: number;
}

const DealSentimentCard: React.FC<DealSentimentCardProps> = ({ 
  onMetricSelect, 
  businessIdentifier,
  timeRange = '7d',
  value: propValue,
  trendValue: propTrendValue 
}) => {
  const [sentimentData, setSentimentData] = useState<DealSentimentSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSentimentData = async () => {
      // If prop values are provided, don't fetch from API (for backward compatibility)
      if (propValue !== undefined && propTrendValue !== undefined) {
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const data = await metricsService.getDealSentimentSummary(businessIdentifier, timeRange);
        setSentimentData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load deal sentiment data.');
        console.error('Error fetching sentiment data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSentimentData();
  }, [businessIdentifier, timeRange, propValue, propTrendValue]);

  const handleCompare = () => {
    if (onMetricSelect) {
      onMetricSelect("Deal Sentiment");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Text color="red.500" textAlign="center">{error}</Text>
      </Box>
    );
  }

  // Use prop values if provided, otherwise use API data, otherwise use fallback
  const displayValue = propValue !== undefined ? propValue : 
    (sentimentData?.average_sentiment_score || 70);
  const trendDisplayValue = propTrendValue !== undefined ? propTrendValue :
    (sentimentData?.trend !== undefined ? sentimentData.trend : -5);

  return (
    <MetricCard
      title="Deal Sentiment"
      value={displayValue} 
      trendValue={trendDisplayValue}
      onCompare={handleCompare}
    />
  );
};

export default DealSentimentCard; 